import { Component, Input, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { getQuestionnaireTemplateConfigByPath } from 'src/app/constants/questionnaire-template-registry';
import { IModule } from 'src/app/interfaces/IModule';
import { IPatientDetailed } from 'src/app/interfaces/Detailed/IPatientDetailed';
import { selectMenu } from 'src/app/selectors/menu.selector';
import { PatientService } from 'src/app/services/patient.service';
import {
  QuestionnaireNavigationService,
  QuestionnaireScreenState,
} from 'src/app/services/questionnaireNavigation.service';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { paths } from 'src/component-paths/component-paths';
import { Subject, takeUntil } from 'rxjs';
import { PatientsHeaderComponent } from '../patients-header/patients-header.component';
import { IAdmissionReasonSummary } from 'src/app/interfaces/AdmissionPatient/IAdmissionReasonSummary';
import { CarePlanService } from 'src/app/services/carePlan.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css'],
  standalone: false,
})
export class ProfileComponent {
  @ViewChild(PatientsHeaderComponent) patientsHeader!: PatientsHeaderComponent;
  public menuData?: IModule;
  public isCollapsed: boolean = false;

  public selectedOption?: string;
  public lastSelectedOption?: string;
  public patientAdmissionReasonSummary?: IAdmissionReasonSummary;
  public activeQuestionnaireScreen: QuestionnaireScreenState | null = null;

  componentPaths = paths;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly patientService: PatientService,
    private readonly store: Store,
    private readonly questionnaireNavigationService: QuestionnaireNavigationService,
    private readonly eocService: EpisodeOfCareService,
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly carePlanService: CarePlanService,
  ) {}

  public patientID: string = '';
  public eocID?: string;

  public patientInfo?: IPatientDetailed;

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  async updateSelectedOption(option: string) {
    if (!(await this.canLeaveCarePlanSection(option))) {
      return;
    }

    this.syncQuestionnaireNavigationContext(option);

    this.eocService.showPatientInformationSub.next(false);
    this.selectedOption = option;
    this.lastSelectedOption = option;
  }

  async clickItem(m: IModule) {
    if (m.path != this.componentPaths.pilotStudy) {
      await this.updateSelectedOption(m.path);
      this.scrollToSection(m.path);
    }
  }

  activeSubmenu(): string {
    return this.findTopLevelMenuPath(this.selectedOption) ?? '';
  }

  getSubmenu(menu: string): IModule[] | undefined {
    return this.menuData?.childModules?.find((d) => d.path === menu)
      ?.childModules;
  }

  getGynecologySubMenu(menu: string): IModule[] | undefined {
    const foundModule = this.menuData?.childModules?.find(
      (d) => d.path === menu
    )?.childModules;

    if (foundModule) {
      const childWithSubmodules = foundModule.find(
        (child) => child.childModules && child.childModules.length > 0
      );
      return childWithSubmodules
        ? childWithSubmodules.childModules
        : foundModule;
    }
    return undefined;
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      // menu, patient header, title, margin
      const offset = element.offsetTop - 120 - 66 - 64 - 16;
      window.scroll({
        top: offset,
        behavior: 'smooth',
      });
    }
  }

  private async canLeaveCarePlanSection(nextOption?: string): Promise<boolean> {
    if (
      this.activeSubmenu() !== this.componentPaths.healthCarePlan ||
      nextOption === this.componentPaths.healthCarePlan
    ) {
      return true;
    }

    return this.carePlanService.confirmDiscardUnsavedChanges();
  }

  private destroy$ = new Subject<void>();
  public showPatientInformation: boolean = false;

  // ngAfterViewInit() {
  //   if (this.patientsHeader) {
  //     this.patientsHeader.triggerChangeDetection();
  //   }
  // }

  ngOnInit() {
    if (window.innerWidth <= 1919) {
      this.isCollapsed = true;
    }

    this.questionnaireNavigationService.screen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((screen) => {
        this.activeQuestionnaireScreen =
          screen?.source === 'profile' ? screen : null;
      });

    this.eocService.showPatientInformationSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showInfo: boolean) => {
        this.showPatientInformation = showInfo;
        if (showInfo) {
          this.lastSelectedOption = this.selectedOption;
          this.selectedOption = undefined; // Clear selected option when showing AdmissionComponent
        } else {
          // Restore the previous selection when hiding AdmissionComponent
          this.selectedOption = this.lastSelectedOption;
        }
      });

    // Reset Episode of Care and Visit ID upon rendering
    // the profile component
    this.eocService.currentEocId.set('');
    this.eocService.currentVisitId.set('');

    this.patientService.patientInfoSub.next({});
    this.patientService.patientLoadingSub.next(true);

    this.route.paramMap.subscribe((params) => {
      this.patientID = params.get('patientID') || '';
      this.eocID = params.get('eocID') ?? undefined;
    });

    this.eocID && this.eocService.currentEocId.set(this.eocID);

    this.patientService.patientIDsignal.set(this.patientID);

    this.patientService.getPatientByID(this.patientID).subscribe({
      next: (response) => {
        this.patientService.patientInfoSub.next(response?.data!);
        this.patientService.patientLoadingSub.next(false);
        this.patientInfo = response?.data; // Assuming data is the property you want

        // Get profile module
        // Set selected option
        this.store.select(selectMenu).subscribe((data) => {
          this.menuData = this.eocID
            ? data
                ?.find((d) => d.path == 'inPatients')
                ?.childModules?.find((d) => d.path === 'inPatientsProfile')
              ? data
                  ?.find((d) => d.path == 'inPatients')
                  ?.childModules?.find((d) => d.path === 'inPatientsProfile')!
              : data
                  ?.find((d) => d.path == 'allPatientsPilotStudy')
                  ?.childModules?.find((d) => d.path === 'pilotStudyProfile')!
            : data
                .find((d) => d.path == 'allPatients')
                ?.childModules?.find((d) => d.path === 'allPatientsProfile');

          // If not female
          // Remove Gynecology TAB (ID: 51)
          if (
            this.patientInfo?.gender?.toLowerCase() != 'female' &&
            this?.menuData?.childModules
          ) {
            this.menuData = {
              ...this.menuData,
              childModules:
                this.menuData?.childModules?.filter(
                  (c) => c.path != paths.gynaecology
                ) || [],
            };
          }

          // if the first option is pilot study (44)
          // have tracheostomy2 (49) as preselected
          if (this.menuData?.childModules?.at(0)?.path == paths.pilotStudy) {
            this.selectedOption = this.menuData?.childModules?.at(0)?.path;
          } else if (
            this.menuData?.childModules?.at(0)?.childModules.length! == 0
          ) {
            this.selectedOption = this.menuData?.childModules?.at(0)?.path;
          } else {
            this.selectedOption =
              this.menuData?.childModules?.at(0)?.childModules?.at(0)?.path ||
              this.menuData?.childModules?.at(0)?.path ||
              this.menuData?.path ||
              '';
          }

          const pendingSection =
            this.questionnaireNavigationService.consumeReturnToSection();

          if (pendingSection) {
            this.selectedOption = pendingSection;
            this.lastSelectedOption = pendingSection;
            setTimeout(() => this.scrollToSection(pendingSection));
          }
        });
      },
      error: (err) => {
        console.error('Error fetching patient information:', err);
      },
    });

    this.patientService.getAdmissionReasonSummary(this.patientID);
  }

  ngOnDestroy() {
    this.eocService.showPatientInformationSub.next(false);
    this.questionnaireNavigationService.closeScreen();
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeQuestionnaireScreen(refreshOverview = true): void {
    this.questionnaireNavigationService.closeScreen();

    if (refreshOverview) {
      this.questionnaireService.invalidateQuestionnaireOverviewCache();
    }
  }

  private syncQuestionnaireNavigationContext(option?: string): void {
    if (!option) {
      return;
    }

    const templateConfig = getQuestionnaireTemplateConfigByPath(option);

    if (templateConfig) {
      this.questionnaireNavigationService.setTemplateContext(templateConfig.id);
      return;
    }

    if (
      option === paths.initialAssessments ||
      option === paths.departmentalQuestionnaires ||
      option === paths.pilotQuestionnaires ||
      option === paths.neurologicalRehabilitation
    ) {
      this.questionnaireNavigationService.setSectionContext(option);
    }
  }

  private findTopLevelMenuPath(option?: string): string | undefined {
    if (!option) {
      return undefined;
    }

    return this.menuData?.childModules?.find((module: IModule) =>
      this.moduleContainsPath(module, option),
    )?.path;
  }

  private moduleContainsPath(module: IModule, option: string): boolean {
    if (module.path === option) {
      return true;
    }

    return (
      module.childModules?.some((child: IModule) =>
        this.moduleContainsPath(child, option),
      ) ?? false
    );
  }

}
